import {
  Box,
  Button,
  ColumnLayout,
  Header,
  Link,
  Modal,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { formatDate } from '@parallel-fluidics/shared/shipping';
import { getFunctions, httpsCallable } from 'firebase/functions';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export function CreatePackageButton({ orderId, shipmentIdx }) {
  const navigate = useNavigate();

  return (
    <Button
      formAction="none"
      onClick={() => navigate(`/package/create/${orderId}?shipIdx=${shipmentIdx}`)}
    >
      Create Package
    </Button>
  );
}
CreatePackageButton.propTypes = {
  orderId: PropTypes.string.isRequired,
  shipmentIdx: PropTypes.number.isRequired,
};

const RefundableStatusSet = new Set([
  'unknown',
  'pre_transit',
]);
export default function PackageTable({
  orderId, shipmentIdx, trackingData, isEmbedded = false,
}) {
  const [pendingShipmentId, setPendingShipmentId] = useState(null);
  const [cancelModalItem, setCancelModalItem] = useState(null);

  const canCancelLabel = useCallback((item) => {
    // if refundStatus is already refunded, we should not allow cancel again
    if (item.refundStatus === 'refunded') return false;
    if (!item.shipmentId) return false;
    if (!item.shippingLabelUrl) return false;
    return RefundableStatusSet.has(item.shippingStatus);
  }, []);

  const handleRefundLabel = useCallback(async () => {
    if (cancelModalItem === null || !cancelModalItem?.shipmentId) return;
    try {
      setPendingShipmentId(cancelModalItem.shipmentId);
      const refundShipmentLabel = httpsCallable(getFunctions(), 'refundShipmentLabelV2');
      await refundShipmentLabel({
        orderId,
        shipmentId: cancelModalItem.shipmentId,
      });
      toast.success(`Shipment ${cancelModalItem.trackingCode || cancelModalItem.shipmentId} refunded successfully.`);
      setCancelModalItem(null);
    } catch (error) {
      toast.error(`Failed to refund shipment label. ${error.message}`);
    } finally {
      setPendingShipmentId(null);
    }
  }, [cancelModalItem, orderId]);

  if (isEmbedded && trackingData.length === 0) {
    return null;
  }
  return (
    <>
      <Table
        variant={isEmbedded ? 'embedded' : 'container'}
        header={!isEmbedded && (
          <Header
            variant="h3"
            actions={
              <CreatePackageButton orderId={orderId} shipmentIdx={shipmentIdx} />
            }
          >
            Packages
          </Header>
        )}
        resizableColumns
        empty={(
          <Box
            margin={{ vertical: 'xs' }}
            textAlign="center"
            color="inherit"
          >
            <SpaceBetween size="m">
              <b>No packages</b>
              {!isEmbedded && <CreatePackageButton orderId={orderId} shipmentIdx={shipmentIdx} />}
            </SpaceBetween>
          </Box>
        )}
        columnDefinitions={[
          {
            id: 'shipDate',
            header: 'Ship Date',
            cell: (item) => (item.shipDate ? formatDate(item.shipDate) : 'N/A'),
          },
          {
            id: 'status',
            header: 'Status',
            // eslint-disable-next-line react/no-unstable-nested-components
            cell: (item) => (item.refundStatus || item.shippingStatus || 'purchased'),
          },
          {
            id: 'carrier',
            header: 'Carrier',
            cell: (item) => item.carrier || 'N/A',
          },
          {
            id: 'amount',
            header: 'Amount',
            cell: (item) => (item.amount ? `$${item.amount}` : 'N/A'),
          },
          {
            id: 'trackingCode',
            header: 'Tracking Code',
            // eslint-disable-next-line react/no-unstable-nested-components
            cell: (item) => (
              item.trackingUrl ? (
                <Link href={item.trackingUrl} external>{item.trackingCode}</Link>
              ) : (
                item.trackingCode || 'N/A'
              )
            ),
          },
          {
            id: 'shippingLabelUrl',
            header: 'Shipping Label',
            // eslint-disable-next-line react/no-unstable-nested-components
            cell: (item) => (
              item.shippingLabelUrl ? (
                <Link href={item.shippingLabelUrl} external>Shipping Label</Link>
              ) : (
                'N/A'
              )
            ),
          },
          {
            id: 'packingSlipUrl',
            header: 'Packing Slip',
            // eslint-disable-next-line react/no-unstable-nested-components
            cell: (item) => (
              item.packingSlipUrl ? (
                <Link href={item.packingSlipUrl} external>Packing Slip</Link>
              ) : (
                'N/A'
              )
            ),
          },
          ...(!isEmbedded ? [{
            id: 'actions',
            header: 'Actions',
            minWidth: 180,
            // eslint-disable-next-line react/no-unstable-nested-components
            cell: (item) => (
              <Button
                formAction="none"
                onClick={() => setCancelModalItem(item)}
                loading={pendingShipmentId === item.shipmentId}
                disabled={!canCancelLabel(item)}
              >
                Cancel Label
              </Button>
            ),
          }] : []),
        ]}
        items={trackingData}
      />
      <Modal
        visible={!!cancelModalItem}
        onDismiss={() => {
          if (!pendingShipmentId) setCancelModalItem(null);
        }}
        header="Cancel and refund shipping label"
        footer={(
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={() => setCancelModalItem(null)}
                disabled={!!pendingShipmentId}
              >
                Close
              </Button>
              <Button
                variant="primary"
                onClick={handleRefundLabel}
                loading={!!pendingShipmentId}
              >
                Confirm refund
              </Button>
            </SpaceBetween>
          </Box>
        )}
      >
        {cancelModalItem && (
          <SpaceBetween direction="vertical" size="m">
            <Box>
              Refund label for tracking code
              {' '}
              <b>{cancelModalItem.trackingCode || cancelModalItem.shipmentId}</b>
              ?
            </Box>
            <ColumnLayout columns={2}>
              <Box>
                Ship Date:
                {' '}
                <b>{cancelModalItem.shipDate ? formatDate(cancelModalItem.shipDate) : 'N/A'}</b>
              </Box>
              <Box>
                Amount:
                {' '}
                <b>{cancelModalItem.amount ? `$${cancelModalItem.amount}` : 'N/A'}</b>
              </Box>
            </ColumnLayout>
          </SpaceBetween>
        )}
      </Modal>
    </>
  );
}

PackageTable.propTypes = {
  orderId: PropTypes.string.isRequired,
  shipmentIdx: PropTypes.number.isRequired,
  trackingData: PropTypes.arrayOf(PropTypes.shape({
    shipDate: PropTypes.string,
    carrier: PropTypes.string,
    trackingCode: PropTypes.string,
    trackingUrl: PropTypes.string,
    shippingLabelUrl: PropTypes.string,
    packingSlipUrl: PropTypes.string,
    shipmentId: PropTypes.string,
    shippingStatus: PropTypes.string,
    refundStatus: PropTypes.string,
  })).isRequired,
  isEmbedded: PropTypes.bool,
};
